import { ref, computed } from "vue";
import { useStore } from "vuex";
import eventBus from "@/src/utils/eventBus.js";
import FormService from "@/src/api/FormService.js";
import ProcessFlowService from "@/src/api/ProcessFlowService.js";
import { standardObjects } from "@/src/constants/objectConstants.js";

function getEntityViewSlug(entityType) {
  const normalizedType = entityType?.toLowerCase();
  const standardObj = standardObjects.find(
    (obj) => obj.slug === normalizedType || obj.viewSlug === normalizedType,
  );
  return standardObj?.viewSlug || normalizedType;
}

function isStandardObject(entityType) {
  const normalizedType = entityType?.toLowerCase();
  return standardObjects.some(
    (obj) => obj.slug === normalizedType || obj.viewSlug === normalizedType,
  );
}

// Resolves custom object slugs to UUIDs for process flow triggers.
function resolveEntityTypeForProcessFlow(store, entityType) {
  if (!entityType || isStandardObject(entityType)) {
    return entityType;
  }
  if (entityType.includes("-") && entityType.length === 36) {
    return entityType;
  }
  try {
    const schema = store?.getters?.["schema/getSchemaBySlug"]?.(entityType);
    if (schema?.object?.id) {
      return schema.object.id;
    }
  } catch (e) {
    console.error(`Failed to resolve object type for "${entityType}", falling back to slug:`, e);
  }
  return entityType;
}

function filterExecutionsByTriggerTiming(executions) {
  const beforeTrigger = executions.filter(
    (e) => e.status === "paused" && e.form && e.load_form_before_trigger === true,
  );
  const afterTrigger = executions.filter(
    (e) => e.status === "paused" && e.form && e.load_form_before_trigger !== true,
  );
  return { beforeTrigger, afterTrigger };
}

const beforeTriggerEntityContext = ref(null);
const isExecuting = ref(false);

const executionFlowState = ref({
  active: false,
  executions: [],
  currentExecutionIndex: 0,
  currentExecution: null,
  entityType: null,
  entityId: null,
  eventType: null,
  callbacks: null,
  collectedFormData: {},
});

const formDrawerState = ref({
  visible: false,
  formId: null,
  formVersion: null,
  nodeId: null,
  executionId: null,
  entityType: null,
  entityId: null,
  eventType: null,
  processFlowId: null,
  isBlocking: false,
  callbacks: null,
  triggerId: 0,
  isEditMode: false,
  submissionId: null,
  initialValues: {},
});

const hideFormDrawer = () => {
  formDrawerState.value = {
    visible: false,
    formId: null,
    formVersion: null,
    nodeId: null,
    executionId: null,
    entityType: null,
    entityId: null,
    eventType: null,
    processFlowId: null,
    isBlocking: false,
    callbacks: null,
    triggerId: 0,
    isEditMode: false,
    submissionId: null,
    initialValues: {},
  };
};

const resetFlow = () => {
  executionFlowState.value = {
    active: false,
    executions: [],
    currentExecutionIndex: 0,
    currentExecution: null,
    entityType: null,
    entityId: null,
    eventType: null,
    callbacks: null,
    collectedFormData: {},
  };
  hideFormDrawer();
};

const completeFlow = () => {
  const state = executionFlowState.value;
  if (state.callbacks?.onAllComplete) {
    state.callbacks.onAllComplete(state.collectedFormData);
  }
  resetFlow();
};

const showFormForExecution = async (execution) => {
  const { form_id, node_id } = execution.form;

  try {
    // Fetch BOTH: current form details (for name) AND published version (for schema)
    const [formDetailsResponse, formVersionResponse] = await Promise.all([
      FormService.forms.fetchFormDetails(form_id),
      FormService.versions.getPublishedVersion(form_id),
    ]);

    if (
      !formVersionResponse?.success ||
      !formVersionResponse?.version
    ) {
      console.error("Failed to load form version for form:", form_id);
      executionFlowState.value.currentExecutionIndex++;
      processNextExecution();
      return;
    }

    const version = formVersionResponse.version;

    // Override the schema name with the current form name
    if (formDetailsResponse?.success && formDetailsResponse?.form?.name && version.schema_json) {
      version.schema_json = {
        ...version.schema_json,
        name: formDetailsResponse.form.name,
      };
    }

    // Emit event to deselect any active fields before showing drawer
    eventBus.emit("deselect-active-fields");

    formDrawerState.value = {
      visible: true,
      formId: form_id,
      formVersion: version,
      nodeId: node_id,
      executionId: execution.execution_id,
      entityType: executionFlowState.value.entityType,
      entityId: executionFlowState.value.entityId,
      eventType: executionFlowState.value.eventType,
      processFlowId: execution.process_flow_id,
      isBlocking: true,
      callbacks: null,
      triggerId: Date.now(),
      isEditMode: false,
      submissionId: null,
      initialValues: {},
    };
  } catch (error) {
    console.error("Error loading form:", error);
    executionFlowState.value.currentExecutionIndex++;
    processNextExecution();
  }
};

const processNextExecution = async () => {
  const state = executionFlowState.value;

  if (state.currentExecutionIndex >= state.executions.length) {
    completeFlow();
    return;
  }

  const execution = state.executions[state.currentExecutionIndex];
  state.currentExecution = execution;

  if (execution.status === "completed") {
    state.currentExecutionIndex++;
    processNextExecution();
    return;
  }

  if (execution.status === "paused" && execution.form) {
    await showFormForExecution(execution);
  } else {
    state.currentExecutionIndex++;
    processNextExecution();
  }
};

export function useFormTrigger() {
  const store = useStore();

  const showFormDrawer = (
    formId,
    formVersion,
    entityType,
    entityId,
    eventType,
    options = {}
  ) => {
    // Emit event to deselect any active fields before showing drawer
    eventBus.emit("deselect-active-fields");

    formDrawerState.value = {
      visible: true,
      formId,
      formVersion,
      nodeId: null,
      executionId: null,
      entityType,
      entityId,
      eventType,
      processFlowId: options.processFlowId || null,
      isBlocking: options.isBlocking || false,
      callbacks: {
        onSubmitSuccess: options.onSubmitSuccess,
        onCancel: options.onCancel,
      },
      triggerId: Date.now(),
      isEditMode: false,
      submissionId: null,
      initialValues: options.initialValues || {},
    };
  };

  const showFormDrawerForEdit = (options) => {
    const {
      formId,
      submissionId,
      entityType,
      entityId,
      initialValues,
      onSubmitSuccess,
      onCancel,
    } = options;

    eventBus.emit("deselect-active-fields");

    formDrawerState.value = {
      visible: true,
      formId,
      formVersion: null,
      nodeId: null,
      executionId: null,
      entityType,
      entityId,
      eventType: null,
      processFlowId: null,
      isBlocking: true,
      callbacks: { onSubmitSuccess, onCancel },
      triggerId: Date.now(),
      isEditMode: true,
      submissionId,
      initialValues: initialValues || {},
    };
  };

  const startMultiExecutionFlow = (
    executions,
    entityType,
    entityId,
    eventType,
    options = {}
  ) => {
    executionFlowState.value = {
      active: true,
      executions,
      currentExecutionIndex: 0,
      currentExecution: null,
      entityType,
      entityId,
      eventType,
      callbacks: {
        onAllComplete: options.onAllComplete,
        onCancel: options.onCancel,
      },
      collectedFormData: {},
    };

    processNextExecution();
  };

  const handleFormSubmitSuccess = async (
    formSubmissionData,
    submitResponse
  ) => {
    const state = executionFlowState.value;

    if (formSubmissionData) {
      Object.assign(state.collectedFormData, formSubmissionData);
    }

    if (submitResponse.status === "paused" && submitResponse.form) {
      const updatedExecution = {
        ...state.currentExecution,
        form: submitResponse.form,
      };
      state.currentExecution = updatedExecution;
      await showFormForExecution(updatedExecution);
    } else {
      state.currentExecutionIndex++;
      const hasMoreFormsToShow = state.executions
        .slice(state.currentExecutionIndex)
        .some((exec) => exec.status === "paused" && exec.form);

      if (!hasMoreFormsToShow) {
        hideFormDrawer();
      }
      processNextExecution();
    }
  };

  const handleFormCancel = () => {
    const state = executionFlowState.value;
    if (state.callbacks?.onCancel) {
      state.callbacks.onCancel();
    }
    resetFlow();
  };

  const isFlowActive = computed(() => executionFlowState.value.active);

  const executeBeforeTrigger = async (entityType, entityId, eventType, updatedAttributes = []) => {
    const resolvedEntityType = resolveEntityTypeForProcessFlow(store, entityType);
    const viewSlug = getEntityViewSlug(resolvedEntityType);

    beforeTriggerEntityContext.value = {
      entityType: resolvedEntityType,
      viewSlug,
      entityId,
      eventType,
      updatedAttributes,
    };

    try {
      isExecuting.value = true;
      let response;
      try {
        response = await ProcessFlowService.execute(
          viewSlug,
          entityId,
          eventType,
          updatedAttributes,
          "before",
        );
      } finally {
        isExecuting.value = false;
      }

      if (!response.success || !response.executions || response.executions.length === 0) {
        return { proceed: true, cancelled: false };
      }

      const { beforeTrigger } = filterExecutionsByTriggerTiming(response.executions);

      if (beforeTrigger.length === 0) {
        return { proceed: true, cancelled: false };
      }

      return new Promise((resolve) => {
        startMultiExecutionFlow(beforeTrigger, viewSlug, entityId, eventType, {
          onAllComplete: () => {
            resolve({ proceed: true, cancelled: false });
          },
          onCancel: () => {
            beforeTriggerEntityContext.value = null;
            resolve({ proceed: false, cancelled: true });
          },
        });
      });
    } catch (error) {
      console.error("Error in executeBeforeTrigger:", error);
      return { proceed: true, cancelled: false };
    }
  };

  const executeAfterTrigger = async (entityId) => {
    const context = beforeTriggerEntityContext.value;
    if (!context) {
      return { success: true, cancelled: false };
    }

    const viewSlug = context.viewSlug || context.entityType;
    const eventType = context.eventType;
    const updatedAttributes = context.updatedAttributes || [];

    try {
      const response = await ProcessFlowService.execute(
        viewSlug,
        entityId,
        eventType,
        updatedAttributes,
        "after",
      );

      if (!response.success || !response.executions || response.executions.length === 0) {
        return { success: true, cancelled: false };
      }

      const { afterTrigger } = filterExecutionsByTriggerTiming(response.executions);

      if (afterTrigger.length === 0) {
        return { success: true, cancelled: false };
      }

      return new Promise((resolve) => {
        startMultiExecutionFlow(afterTrigger, viewSlug, entityId, eventType, {
          onAllComplete: () => {
            resolve({ success: true, cancelled: false });
          },
          onCancel: () => {
            resolve({ success: false, cancelled: true });
          },
        });
      });
    } catch (error) {
      console.error("Error in executeAfterTrigger:", error);
      return { success: true, cancelled: false };
    } finally {
      beforeTriggerEntityContext.value = null;
    }
  };

  const resetEntityFormTriggers = () => {
    beforeTriggerEntityContext.value = null;
  };

  return {
    formDrawerState,
    executionFlowState,
    showFormDrawer,
    showFormDrawerForEdit,
    hideFormDrawer,
    startMultiExecutionFlow,
    handleFormSubmitSuccess,
    handleFormCancel,
    resetFlow,
    isFlowActive,
    isExecuting,
    executeBeforeTrigger,
    executeAfterTrigger,
    resetEntityFormTriggers,
  };
}
